import React, { useState, useEffect } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { X, RefreshCw, Download, Smartphone, Share, MoreVertical } from 'lucide-react'
import { useTranslation } from '../lib/translations'
import { usePwaInstall } from '../hooks/usePwaInstall'

function ReloadPrompt({ inGame = false }: { inGame?: boolean }) {
  const { language } = useTranslation();
  const { isInstallable, isStandalone, isInstalled, triggerInstall } = usePwaInstall();
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const [dismissedInstall, setDismissedInstall] = useState(() => {
    try {
      return localStorage.getItem('pwa_install_dismissed') === 'true';
    } catch {
      return false;
    }
  });
  const [isCookieBannerActive, setIsCookieBannerActive] = useState(() => {
    try {
      return !localStorage.getItem('cookie_consent');
    } catch {
      return false;
    }
  });

  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      if (import.meta.env.DEV) {
        console.log('[PWA] SW registered:', r);
      }
    },
    onRegisterError(error) {
      // In automated crawlers (such as Googlebot / Google Search Console WRS) and restricted sandboxes,
      // navigator.serviceWorker.register() is intentionally rejected by design (e.g., wrsParams.serviceWorkers).
      // We gracefully swallow the rejection here in production so no error or warning is reported to GSC or user console.
      if (import.meta.env.DEV) {
        console.warn('[PWA] SW registration failed (expected in crawlers/unsupported envs):', error);
      }
    },
  });

  useEffect(() => {
    const handleShowGuide = () => setShowGuideModal(true);
    window.addEventListener('show-pwa-install-guide', handleShowGuide);
    return () => window.removeEventListener('show-pwa-install-guide', handleShowGuide);
  }, []);

  useEffect(() => {
    const handleCookieBannerChange = (e: Event) => {
      const customEvent = e as CustomEvent<{ active: boolean }>;
      if (customEvent.detail && typeof customEvent.detail.active === 'boolean') {
        setIsCookieBannerActive(customEvent.detail.active);
      } else {
        try {
          setIsCookieBannerActive(!localStorage.getItem('cookie_consent'));
        } catch {
          setIsCookieBannerActive(false);
        }
      }
    };

    window.addEventListener('cookie-banner-change', handleCookieBannerChange);
    return () => window.removeEventListener('cookie-banner-change', handleCookieBannerChange);
  }, []);

  const handleInstallClick = async () => {
    const res = await triggerInstall();
    if (res === 'unavailable') {
      setShowGuideModal(true);
    }
  };

  const close = () => {
    setOfflineReady(false);
    setNeedRefresh(false);
    setSessionDismissed(true);
    
    // If this was the install prompt (not an update or offline ready message)
    if (!needRefresh && !offlineReady) {
      try {
        localStorage.setItem('pwa_install_dismissed', 'true');
        setDismissedInstall(true);
      } catch {}
    }
  };

  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isDevHost = typeof window !== 'undefined' && (
    window.location.hostname.includes('run.app') || 
    window.location.hostname.includes('localhost') || 
    window.self !== window.top
  );

  const showBanner = !sessionDismissed && !isCookieBannerActive && (
    (needRefresh || offlineReady) || 
    (!inGame && !isStandalone && !isInstalled && !dismissedInstall)
  );

  return (
    <>
      {/* Floating PWA / SW Prompt Banner */}
      {showBanner && (
        <div className="fixed bottom-3 right-3 left-3 sm:left-auto sm:right-4 z-[9990] p-2.5 sm:p-3 rounded-xl sm:rounded-2xl shadow-xl bg-slate-900/95 backdrop-blur-md border border-cyan-500/30 text-white sm:max-w-sm flex items-center justify-between gap-2 sm:gap-3 animate-fade-in">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0 text-cyan-400">
              <Smartphone className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              {needRefresh ? (
                <span className="font-bold text-xs sm:text-sm text-amber-400 block truncate">
                  {language === 'tr' ? 'Yeni Güncelleme!' : 'New Update!'}
                </span>
              ) : offlineReady ? (
                <span className="font-bold text-xs sm:text-sm text-emerald-400 block truncate">
                  {language === 'tr' ? 'Çevrimdışı Hazır!' : 'Offline Ready!'}
                </span>
              ) : (
                <span className="font-bold text-xs sm:text-sm text-cyan-300 block truncate">
                  {language === 'tr' ? 'Uygulamayı Yükleyin' : 'Install App'}
                </span>
              )}
              <span className="text-[10px] sm:text-xs text-slate-400 block truncate">
                {language === 'tr' ? 'Ana ekrana ekleyip tam ekran oynayın' : 'Add to home screen'}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5 shrink-0">
            {!needRefresh && (
              <button 
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 rounded-lg font-bold transition-all text-xs shadow-[0_0_15px_rgba(6,182,212,0.3)] active:scale-95 whitespace-nowrap"
                onClick={handleInstallClick}
              >
                <Download className="w-3.5 h-3.5" />
                <span>{language === 'tr' ? 'Yükle' : 'Install'}</span>
              </button>
            )}

            {needRefresh && (
              <button 
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-lg font-bold transition-colors text-xs whitespace-nowrap"
                onClick={() => updateServiceWorker(true)}
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>{language === 'tr' ? 'Güncelle' : 'Update'}</span>
              </button>
            )}

            <button onClick={close} className="text-slate-500 hover:text-slate-300 transition-colors p-1" title={language === 'tr' ? 'Kapat' : 'Close'}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Manual Installation Guide Modal if native prompt is unavailable or deferred */}
      {showGuideModal && (
        <div className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-cyan-500/30 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5 animate-fade-in relative text-white">
            <button 
              onClick={() => setShowGuideModal(false)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-full bg-slate-800/50"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center text-cyan-400">
                <Smartphone className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  {language === 'tr' ? 'Uygulamayı Ana Ekrana Ekle' : 'Add App to Home Screen'}
                </h3>
                <p className="text-xs text-cyan-400/80">pisti.online</p>
              </div>
            </div>

            {isDevHost && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
                <p className="text-amber-200/90 text-[11px] leading-relaxed">
                  {language === 'tr' 
                    ? 'Şu an geliştirici test linkindesiniz. Güvenlik duvarı nedeniyle otomatik kurulum çalışmaz. Gerçek alan adınıza (pisti.online) yayına alındığında her şey otomatik çalışacaktır. Şimdilik manuel kurulum adımlarını izleyebilirsiniz:'
                    : 'You are on a dev test link. Native install is blocked by the firewall. It will work automatically on your real domain (pisti.online). For now, you can follow the manual installation steps:'}
                </p>
              </div>
            )}

            {isIOS ? (
              <div className="space-y-3 bg-slate-950/60 p-4 rounded-2xl border border-white/5 text-xs text-slate-300">
                <p className="font-semibold text-white flex items-center gap-2">
                  <Share className="w-4 h-4 text-cyan-400" />
                  {language === 'tr' ? 'iOS (Safari) Adımları:' : 'iOS (Safari) Steps:'}
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-400">
                  <li>{language === 'tr' ? 'Safari alt menüsündeki Paylaş (Share) butonuna dokunun.' : 'Tap the Share button in Safari toolbar.'}</li>
                  <li>{language === 'tr' ? 'Açılan menüde "Ana Ekrana Ekle" seçeneğini seçin.' : 'Select "Add to Home Screen" from menu.'}</li>
                  <li>{language === 'tr' ? 'Sağ üstteki "Ekle" butonuna basarak tamamlayın.' : 'Tap "Add" in the top right corner.'}</li>
                </ol>
              </div>
            ) : (
              <div className="space-y-3 bg-slate-950/60 p-4 rounded-2xl border border-white/5 text-xs text-slate-300">
                <p className="font-semibold text-white flex items-center gap-2">
                  <MoreVertical className="w-4 h-4 text-cyan-400" />
                  {language === 'tr' ? 'Android (Chrome) Adımları:' : 'Android (Chrome) Steps:'}
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-400">
                  <li>{language === 'tr' ? 'Chrome sağ üst köşesindeki 3 nokta (⋮) menüsüne dokunun.' : 'Tap the 3 dots (⋮) menu in top right Chrome.'}</li>
                  <li>{language === 'tr' ? '"Uygulamayı Yükle" veya "Ana Ekrana Ekle" seçeneğine basın.' : 'Tap "Install App" or "Add to Home Screen".'}</li>
                  <li>{language === 'tr' ? 'Ekranda çıkan yükleme onayını onaylayın.' : 'Confirm the installation dialog.'}</li>
                </ol>
              </div>
            )}

            <button 
              onClick={() => setShowGuideModal(false)}
              className="w-full py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold rounded-xl text-sm transition-all"
            >
              {language === 'tr' ? 'Anladım' : 'Got it'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ReloadPrompt;

